import { useEffect, useRef, useState } from "react";

function storageKey(
  workspaceId: string,
  moduleId: "script-vet" | "draft-vet" | "creator-fit",
  requestId?: string
) {
  const scope = requestId ? `request:${requestId}:prompt` : "request-prompt";
  return `ai-workspace:${workspaceId}:${moduleId}:${scope}`;
}

function readStoredPrompt(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredPrompt(key: string, html: string) {
  try {
    localStorage.setItem(key, html);
  } catch {
    return;
  }
}

function promptPlain(html: string) {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function promptHasText(html: string) {
  return promptPlain(html).length > 0;
}

function clearStoredPrompt(key: string) {
  try {
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}:edited`);
  } catch {
    return;
  }
}

/** A new check starts from the workspace master prompt. An open request keeps its own prompt. */
export function useLocalRequestPrompt(
  workspaceId: string,
  moduleId: "script-vet" | "draft-vet" | "creator-fit",
  sourcePrompt: string,
  requestId?: string
) {
  const key = storageKey(workspaceId, moduleId, requestId);
  const editedKey = `${key}:edited`;
  const isNewCheck = !requestId;
  const storedOnLoad = isNewCheck ? "" : readStoredPrompt(key) || "";
  const editedOnLoad =
    !isNewCheck &&
    readStoredPrompt(editedKey) === "1" &&
    promptHasText(storedOnLoad);
  const start = editedOnLoad ? storedOnLoad : sourcePrompt;
  const [promptHtml, setPromptHtml] = useState(start);
  const [initialHtml, setInitialHtml] = useState(start);
  const [seed, setSeed] = useState(start ? 1 : 0);
  const userEdited = useRef(false);

  useEffect(() => {
    if (isNewCheck) {
      clearStoredPrompt(key);
      if (!sourcePrompt || userEdited.current) return;
      setPromptHtml(sourcePrompt);
      setInitialHtml(sourcePrompt);
      setSeed((current) => current + 1);
      return;
    }
    const existing = readStoredPrompt(key) || "";
    const edited =
      readStoredPrompt(editedKey) === "1" && promptHasText(existing);
    if (edited) {
      setPromptHtml(existing);
      setInitialHtml(existing);
      setSeed(1);
      return;
    }
    if (!sourcePrompt) return;
    setPromptHtml(sourcePrompt);
    setInitialHtml(sourcePrompt);
    setSeed((current) => current + 1);
  }, [editedKey, isNewCheck, key, sourcePrompt]);

  const updatePrompt = (html: string) => {
    setPromptHtml(html);
    if (!promptHasText(html)) return;
    if (isNewCheck) {
      if (promptPlain(html) !== promptPlain(sourcePrompt))
        userEdited.current = true;
      return;
    }
    writeStoredPrompt(editedKey, "1");
    writeStoredPrompt(key, html);
  };

  return {
    promptHtml,
    initialHtml,
    updatePrompt,
    contentKey: `${key}:${seed}`,
  };
}
